import omit from 'lodash/omit';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { getMakeModelCount } from 'Services/api/vehicles';
import { useSetMakesCount } from 'Stores/MakesStore/MakesStore';
import { useSetModelsCount } from 'Stores/ModelsStore/ModelsStore';
import type { Filter, FilterQuery } from 'Types/filter';
import { useShouldFetchMakeModelCount } from 'Utilities/hooks/vehicleList/useShouldFetchMakeModelCount';

import useVehicleListQuery from './useVehicleListQuery/useVehicleListQuery';

interface UseMakeModelCountUpdaterParams {
	filters: Filter;
	serverQuery: FilterQuery['serverQuery'];
}

export const useMakeModelCountUpdater = ({ filters, serverQuery }: UseMakeModelCountUpdaterParams): void => {
	const vehicleListQuery = useVehicleListQuery({ serverQuery });
	const setMakesCount = useSetMakesCount();
	const setModelsCount = useSetModelsCount();
	const shouldFetchCount = useShouldFetchMakeModelCount({ filters, vehicleListQuery });

	useDeepCompareEffect(() => {
		// isLoading is used to prevent race conditions
		let isLoading = true;
		if (!shouldFetchCount) {
			return undefined;
		}

		const getCounts = async () => {
			const { makes, models } = await getMakeModelCount({ ...omit(vehicleListQuery, ['timeId']) });

			if (isLoading) {
				setMakesCount(makes);
				setModelsCount(models);
				isLoading = false;
			}
		};

		getCounts().catch(() => {
			isLoading = false;
		});

		return () => {
			isLoading = false;
		};
	}, [vehicleListQuery, setMakesCount, shouldFetchCount]);
};
